import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import styled, { css } from 'styled-components';

import { useMergedRefs } from '@components/component-helpers';
import { Text, TextProps } from '@components/text';
import { Color, theme } from '@styles/theme';
import { clamp } from '@utils/browser';
import { second } from '@utils/milliseconds';

const DEFAULT_SPEED_PX_PER_SECOND = 150;
const DEFAULT_WIDTH_CH = 8;
const DEFAULT_DELAY_SECONDS = 1;
const MIN_WIDTH_CH = 4;
const MAX_WIDTH_CH = 24;
const SHIMMER_OPACITY = 1;
const LINE_TOP_TOLERANCE_PX = 1;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clearShimmerStyles = (element: HTMLElement) => {
  element.style.removeProperty('--shimmer-mask-x');
  element.style.removeProperty('--shimmer-mask-y');
  element.style.removeProperty('--shimmer-line-height');
};

let textMeasureCanvas: HTMLCanvasElement | null = null;

const measureTextWidth = (text: string, font: string) => {
  if (typeof document === 'undefined') {
    return 0;
  }
  if (!textMeasureCanvas) {
    textMeasureCanvas = document.createElement('canvas');
  }
  const context = textMeasureCanvas.getContext('2d');
  if (!context) {
    return 0;
  }
  context.font = font;
  return context.measureText(text).width;
};

type LineRect = {
  width: number;
  top: number;
  height: number;
};

const areLineRectsEqual = (left: LineRect[], right: LineRect[]) =>
  left.length === right.length &&
  left.every((leftLine, index) => {
    const rightLine = right[index];
    return (
      leftLine.width === rightLine.width &&
      leftLine.top === rightLine.top &&
      leftLine.height === rightLine.height
    );
  });

const getLineRects = (element: HTMLElement): LineRect[] => {
  if (typeof document === 'undefined') {
    return [];
  }

  const range = document.createRange();
  range.selectNodeContents(element);

  const elementRect = element.getBoundingClientRect();
  const rects = Array.from(range.getClientRects())
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .sort((a, b) => a.top - b.top || a.left - b.left);

  const lines: LineRect[] = [];
  rects.forEach((rect) => {
    const top = Math.round(rect.top - elementRect.top);
    const lineHeight = Math.round(rect.height);
    const lineWidth = Math.round(rect.width);
    const lastLine = lines[lines.length - 1];

    if (!lastLine || Math.abs(lastLine.top - top) > LINE_TOP_TOLERANCE_PX) {
      lines.push({
        width: lineWidth,
        top,
        height: lineHeight,
      });
    } else {
      lastLine.width = Math.max(lastLine.width, lineWidth);
      lastLine.height = Math.max(lastLine.height, lineHeight);
    }
  });

  return lines;
};

export type ShimmerProps = TextProps & {
  shimmerColor?: Color;
  shimmerSpeed?: number;
  shimmerWidth?: number;
  shimmerDelay?: number;
  isPaused?: boolean;
};

export const Shimmer = forwardRef<HTMLElement, ShimmerProps>(function Shimmer(
  {
    shimmerColor = 'backgroundPrimary',
    shimmerSpeed = DEFAULT_SPEED_PX_PER_SECOND,
    shimmerWidth = DEFAULT_WIDTH_CH,
    shimmerDelay = DEFAULT_DELAY_SECONDS,
    isPaused = false,
    color = 'textPrimary',
    font = 'regular16',
    text,
    children,
    ...rest
  },
  ref,
) {
  const localRef = useRef<HTMLElement>(null);
  const mergedRef = useMergedRefs<HTMLElement>([ref, localRef]);
  const [lineRects, setLineRects] = useState<LineRect[]>([]);
  const [computedFont, setComputedFont] = useState('');
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimestampRef = useRef<number | null>(null);
  const elapsedSecondsRef = useRef(0);
  const contentProps = text !== undefined ? { text } : { children };
  const shimmerTextSource = text !== undefined ? text : children;
  const shimmerText =
    typeof shimmerTextSource === 'string' || typeof shimmerTextSource === 'number'
      ? String(shimmerTextSource)
      : '';
  const clampedWidth = clamp(MIN_WIDTH_CH, shimmerWidth, MAX_WIDTH_CH);
  const speedPxPerSecond = Math.max(shimmerSpeed, 0);
  const delaySeconds = Math.max(shimmerDelay, 0);

  useLayoutEffect(() => {
    const element = localRef.current;
    if (!element) {
      return;
    }

    const updateMetrics = () => {
      const nextFont = getComputedStyle(element).font;
      setComputedFont((prev) => (prev === nextFont ? prev : nextFont));
      const nextLineRects = getLineRects(element);
      setLineRects((prev) => (areLineRectsEqual(prev, nextLineRects) ? prev : nextLineRects));
    };

    updateMetrics();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(updateMetrics);
    observer.observe(element);
    return () => observer.disconnect();
  }, [shimmerText, font]);

  const bandWidthPx = useMemo(() => {
    if (!computedFont) {
      return 0;
    }
    return measureTextWidth('0'.repeat(clampedWidth), computedFont);
  }, [clampedWidth, computedFont]);

  const { lineAnimationData, totalDuration, totalCycleDuration } = useMemo(() => {
    if (!speedPxPerSecond || !bandWidthPx) {
      return { lineAnimationData: [], totalDuration: 0, totalCycleDuration: 0 };
    }

    let offset = 0;
    const data = lineRects.map((line) => {
      const duration = (line.width + bandWidthPx) / speedPxPerSecond;
      const entry = {
        ...line,
        duration,
        offset,
      };
      offset += duration;
      return entry;
    });

    return {
      lineAnimationData: data,
      totalDuration: offset,
      totalCycleDuration: offset + delaySeconds,
    };
  }, [bandWidthPx, delaySeconds, lineRects, speedPxPerSecond]);

  useEffect(() => {
    const element = localRef.current;
    if (!element) {
      return undefined;
    }

    const stopAnimation = (shouldClearStyles: boolean) => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (shouldClearStyles) {
        clearShimmerStyles(element);
      }
    };

    if (isPaused || !lineAnimationData.length || prefersReducedMotion()) {
      stopAnimation(true);
      lastFrameTimestampRef.current = null;
      return undefined;
    }

    lastFrameTimestampRef.current = null;

    const step = (timestamp: number) => {
      if (!lineAnimationData.length || totalDuration <= 0 || totalCycleDuration <= 0) {
        return;
      }

      if (lastFrameTimestampRef.current === null) {
        lastFrameTimestampRef.current = timestamp;
      } else {
        elapsedSecondsRef.current += (timestamp - lastFrameTimestampRef.current) / second;
        lastFrameTimestampRef.current = timestamp;
      }

      const cycleTime = elapsedSecondsRef.current % totalCycleDuration;
      if (cycleTime >= totalDuration) {
        const lastLine = lineAnimationData[lineAnimationData.length - 1];
        element.style.setProperty('--shimmer-mask-x', `${lastLine.width}px`);
        element.style.setProperty('--shimmer-mask-y', `${lastLine.top}px`);
        element.style.setProperty('--shimmer-line-height', `${lastLine.height}px`);
        animationFrameRef.current = requestAnimationFrame(step);
        return;
      }

      let currentLine = lineAnimationData[0];
      for (let index = 0; index < lineAnimationData.length; index += 1) {
        const line = lineAnimationData[index];
        if (cycleTime < line.offset + line.duration) {
          currentLine = line;
          break;
        }
      }

      const progress = (cycleTime - currentLine.offset) / currentLine.duration;
      const xPosition = -bandWidthPx + progress * (currentLine.width + bandWidthPx);

      element.style.setProperty('--shimmer-mask-x', `${xPosition}px`);
      element.style.setProperty('--shimmer-mask-y', `${currentLine.top}px`);
      element.style.setProperty('--shimmer-line-height', `${currentLine.height}px`);

      animationFrameRef.current = requestAnimationFrame(step);
    };

    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      stopAnimation(false);
    };
  }, [bandWidthPx, isPaused, lineAnimationData, totalCycleDuration, totalDuration]);

  return (
    <ShimmerText
      ref={mergedRef}
      color={color}
      font={font}
      {...contentProps}
      aiGradient={false}
      data-shimmer-text={shimmerText}
      $shimmerColor={shimmerColor}
      $shimmerWidth={clampedWidth}
      {...rest}
    />
  );
});

const ShimmerText = styled(Text)<{
  $shimmerColor: Color;
  $shimmerWidth: number;
}>`
  position: relative;
  isolation: isolate;
  --shimmer-band-size: ${({ $shimmerWidth }) => `${$shimmerWidth}ch`};
  --shimmer-mask-x: calc(-1 * var(--shimmer-band-size));
  --shimmer-mask-y: 0px;
  --shimmer-line-height: 100%;

  &::after {
    content: attr(data-shimmer-text);
    position: absolute;
    inset: 0;
    color: transparent;
    pointer-events: none;
    opacity: ${SHIMMER_OPACITY};
    white-space: inherit;
    overflow-wrap: inherit;
    text-align: inherit;
    will-change: mask-position;
  }

  ${({ $shimmerColor }) => {
    const shimmerColorValue = theme.color[$shimmerColor] as string;

    return css`
      &::after {
        color: ${shimmerColorValue};
        -webkit-text-fill-color: ${shimmerColorValue};
        mask-image: linear-gradient(90deg, transparent 0%, #000 40%, #000 60%, transparent 100%);
        -webkit-mask-image: linear-gradient(
          90deg,
          transparent 0%,
          #000 40%,
          #000 60%,
          transparent 100%
        );
        mask-size: var(--shimmer-band-size) var(--shimmer-line-height);
        -webkit-mask-size: var(--shimmer-band-size) var(--shimmer-line-height);
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-position: var(--shimmer-mask-x) var(--shimmer-mask-y);
        -webkit-mask-position: var(--shimmer-mask-x) var(--shimmer-mask-y);
      }
    `;
  }}
`;
